/** Plain-language explanation of the connected system. */
export const howItWorksFunctions = [
  {
    id: 'connect-knowledge',
    title: 'Connect knowledge',
    body: 'Organize sources, concepts and relationships so people can find relevant information and understand its origins. SyNeural’s Semantic Knowledge Platform maintains the connected knowledge behind the workspace.',
    link: { label: 'Explore connected knowledge', href: '/semantic-knowledge' },
    example: 'A researcher opens a finding and reads the exact passage it came from.',
  },
  {
    id: 'coordinate-work',
    title: 'Coordinate work',
    body: 'Bring records, requests, reviews and next steps into a clear workflow. Noetivra Workspace gives each user a focused view of their responsibilities, while shared services connect activity across the organization.',
    link: { label: 'Explore Noetivra', href: '/noetivra' },
    example: 'A caseworker sees a new support request, assigns it and records the outcome.',
  },
  {
    id: 'develop-capability',
    title: 'Develop capability',
    body: 'Connect assessment with feedback, purposeful practice and reassessment. The Capability module uses the Human Capability Framework and relevant CJI or NCJI methods to structure development programs.',
    link: { label: 'Explore human development', href: '/human-capability' },
    example: 'A learner receives feedback, completes a practice activity and returns to a new task.',
  },
]

export const configurationLayers = [
  { name: 'Core', body: 'The intended shared foundation for records, permissions, workflows, integrations and activity history.' },
  { name: 'Workspace', body: 'The visual interface: dedicated workspaces through which each person does their part of the work.' },
  { name: 'Modules', body: 'Reusable functional building blocks, switched on where a solution needs them.' },
  { name: 'Solutions', body: 'Configurations that combine the relevant modules and workflows for one field, such as Government or Clinical.' },
]

export const publicModules = [
  { name: 'Knowledge', purpose: 'Connect sources, concepts and reviewed findings.' },
  { name: 'Capability', purpose: 'Run assessment and development workflows.' },
  { name: 'Services', purpose: 'Coordinate requests, referrals, support plans and follow-up.' },
  { name: 'Credentials', purpose: 'Record and share scoped achievements and qualifications.' },
]

/** The architecture, top-down: from the knowledge foundation to the solutions people use. */
export const architectureLayers = [
  {
    id: 'semantic-knowledge-platform',
    level: 'Knowledge foundation',
    name: 'Semantic Knowledge Platform',
    role: 'Connects and governs sources, concepts and reviewed relationships.',
  },
  {
    id: 'human-capability-framework',
    level: 'Methods',
    name: 'Human Capability Framework',
    role: 'Defines how assessment, review, development and reassessment work, including CJI and NCJI.',
  },
  {
    id: 'noetivra-core',
    level: 'Shared platform',
    name: 'Noetivra Core',
    role: 'The intended common foundation for records, permissions, workflows and integrations.',
  },
  {
    id: 'modules',
    level: 'Functions',
    name: 'Modules',
    role: 'Reusable building blocks: Knowledge, Capability, Services and Credentials.',
  },
  {
    id: 'workspaces',
    level: 'Interface',
    name: 'Noetivra Workspace',
    role: 'Dedicated workspaces that show each person the information and tools for their role.',
  },
  {
    id: 'solutions',
    level: 'Applications',
    name: 'Solutions',
    role: 'Configurations for government, research, education, workforce, leadership, community and clinical work.',
  },
]

/** Detail for the three foundational layers. */
export const foundationDetails = {
  'semantic-knowledge-platform': {
    body: 'Everything starts with organized knowledge. SKP connects papers, policies, program rules and other sources to the concepts they describe and the relationships between them. AI-assisted proposals stay separate from approved knowledge, and every change keeps its review history.',
    points: ['Source passages linked to every relationship', 'Controlled concepts and terminology', 'Review and version history'],
    link: { label: 'Explore the Semantic Knowledge Platform', href: '/semantic-knowledge' },
  },
  'human-capability-framework': {
    body: 'Where a solution involves developing people, the framework supplies the methods: how tasks are assessed, how findings are reviewed by people, and how development and reassessment connect. CJI and NCJI sit within it. It applies where capability development is part of the work; research and clinical solutions don’t require it.',
    points: ['Assess → Review → Develop → Reassess', 'CJI and NCJI methods and models', 'Human review of findings'],
    link: { label: 'Explore the Human Capability Framework', href: '/human-capability' },
  },
  'noetivra-core': {
    body: 'Noetivra Core is the intended shared foundation that every workspace draws on. It holds records, manages who can see and do what, runs workflows and connects to an organization’s existing services, so different teams work from the same organized information.',
    points: ['Records and activity history', 'Role-based permissions', 'Workflows and integrations'],
    link: { label: 'Explore Noetivra', href: '/noetivra' },
  },
} as const

/** Paired workspaces: examples of one configurable environment. */
export const rolePairs = [
  {
    setting: 'Government',
    workspaces: ['Citizen Workspace', 'Administration Control Panel'],
    note: 'A citizen requests support and follows an application; an administrator coordinates the response.',
  },
  {
    setting: 'Research',
    workspaces: ['Research Workspace', 'Curation & Administration'],
    note: 'A researcher explores findings while a curator reviews the knowledge behind them.',
  },
  {
    setting: 'Education',
    workspaces: ['Learner Workspace', 'Educator Workspace'],
    note: 'A learner acts on feedback while an educator follows progress and refines support.',
  },
]

/** Modules as presented on How It Works. */
export const configurableModules = [
  { name: 'Knowledge', enables: 'Organize sources, explore relationships and review findings.' },
  { name: 'Capability', enables: 'Connect assessments, feedback, development plans and reassessment.' },
  { name: 'Services', enables: 'Coordinate applications, referrals, support plans and follow-up.' },
  { name: 'Credentials', enables: 'Record and share achievements with a clear description of what they establish.' },
]

/** Example government workflow on How It Works. */
export const exampleWorkflow = [
  { step: 'Request', body: 'A resident asks for help finding a training place they can reach.' },
  { step: 'Information check', body: 'The team reviews what is already known and asks only for what is missing.' },
  { step: 'Assigned team', body: 'The request has a clear owner and a visible next step.' },
  { step: 'Coordinated support', body: 'A referral shares the information the resident has agreed to provide.' },
  { step: 'Recorded decision', body: 'Both sides see the decision, its reason and what happens next.' },
  { step: 'Progress review', body: 'The team follows up on what helped and what barriers remain.' },
]

export const configurationStatus =
  'Interactive demos show these workspaces and workflows with fictional records. The shared Core and the module structure describe the production platform under development; solution-specific functions such as clinical review and citizen administration are configured alongside the shared modules.'
